import React, { useEffect, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, TrendingUp, Users, DollarSign, CheckCircle } from 'lucide-react';
import { DealCard } from '@/components/DealCard';
import { CategoryCard } from '@/components/CategoryCard';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_deals: 0, active_users: 0, total_savings_aed: 0 });
  const [categories, setCategories] = useState([]);
  const [featuredDeals, setFeaturedDeals] = useState([]);
  const [recentDeals, setRecentDeals] = useState([]);
  const [brands, setBrands] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmirate, setSelectedEmirate] = useState('All UAE');
  const [loading, setLoading] = useState(true);

  const emirates = ['All UAE', 'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

  // Structured Data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Yalla ! Lets Save.",
      "url": typeof window !== 'undefined' ? window.location.origin : "https://www.yallasavings.com",
      "description": "UAE's leading platform for discovering the best deals, discounts, and offers",
      "potentialAction": {
        "@type": "SearchAction",
        "target": typeof window !== 'undefined' ? `${window.location.origin}/deals?q={search_term_string}` : "https://www.yallasavings.com/deals?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, categoriesRes, featuredRes, dealsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/deals/featured?limit=6`),
        axios.get(`${API}/deals?limit=8`),
        axios.get(`${API}/brands?featured=true`)
      ]);

      setStats(statsRes.data);
      setCategories(categoriesRes.data);
      setFeaturedDeals(featuredRes.data);
      setRecentDeals(dealsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/deals?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleEmirateChange = (emirate) => {
    setSelectedEmirate(emirate);
    if (emirate !== 'All UAE') {
      navigate(`/deals?location=${encodeURIComponent(emirate)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6bTAtMzBjMC0yLjIxIDEuNzktNCA0LTRzNCAxLjc5IDQgNC0xLjc5IDQtNCA0LTQtMS43OS00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="hero-title">
              Discover Amazing Deals<br />
              <span className="text-yellow-300">Across the UAE</span>
            </h1>
            <p className="text-xl md:text-2xl text-emerald-50 mb-8" data-testid="hero-subtitle">
              Save big on dining, shopping, travel, entertainment and more
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search for deals, brands, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-14 text-gray-900 text-lg"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                size="lg" 
                className="h-14 px-8 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
                data-testid="search-button"
              >
                Search Deals
              </Button>
            </form>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto pt-8" data-testid="stats-section">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold" data-testid="total-deals-stat">{stats.total_deals}+</div>
                <div className="text-emerald-100 text-sm md:text-base">Active Deals</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold" data-testid="active-users-stat">{stats.active_users}+</div>
                <div className="text-emerald-100 text-sm md:text-base">Happy Users</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold" data-testid="total-savings-stat">
                  {stats.total_savings_aed.toLocaleString()} AED
                </div>
                <div className="text-emerald-100 text-sm md:text-base">Total Savings</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Quick Access */}
      <section className="py-16 bg-white" data-testid="categories-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="categories-heading">
              Browse by Category
            </h2>
            <p className="text-gray-600 text-lg" data-testid="categories-description">
              Find deals in your favorite categories
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Location/Emirate Selector */}
      <section className="py-12 bg-gray-100" data-testid="location-selector-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="location-heading">
              Select Your Emirate
            </h2>
            <p className="text-gray-600" data-testid="location-description">Find deals near you</p>
          </div>

          <Tabs value={selectedEmirate} onValueChange={handleEmirateChange} className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center bg-white h-auto p-2 gap-2">
              {emirates.map((emirate) => (
                <TabsTrigger
                  key={emirate}
                  value={emirate}
                  className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                  data-testid={`emirate-${emirate.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {emirate}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Featured/Trending Deals */}
      <section className="py-16 bg-white" data-testid="featured-deals-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="featured-heading">
                <TrendingUp className="inline mr-2 text-emerald-600" />
                Featured Deals
              </h2>
              <p className="text-gray-600 text-lg" data-testid="featured-description">
                Don't miss these amazing offers
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/deals')}
              data-testid="view-all-deals-button"
            >
              View All
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading deals...</div>
          ) : (
            <Carousel className="w-full">
              <CarouselContent className="-ml-4">
                {featuredDeals.map((deal) => (
                  <CarouselItem key={deal.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <DealCard deal={deal} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0" />
              <CarouselNext className="right-0" />
            </Carousel>
          )}
        </div>
      </section>

      {/* Recent Deals Grid */}
      <section className="py-16 bg-gray-50" data-testid="recent-deals-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="recent-heading">
              Latest Deals
            </h2>
            <p className="text-gray-600 text-lg" data-testid="recent-description">
              Freshly added deals just for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {recentDeals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate('/deals')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="browse-all-deals-button"
            >
              Browse All Deals
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="how-it-works-heading">
              How It Works
            </h2>
            <p className="text-gray-600 text-lg" data-testid="how-it-works-description">
              Start saving in 4 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4" data-testid="step-1">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Search className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold">1. Browse Deals</h3>
              <p className="text-gray-600">Explore thousands of deals across all categories and emirates</p>
            </div>

            <div className="text-center space-y-4" data-testid="step-2">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
                <Badge className="w-10 h-10 bg-teal-600" />
              </div>
              <h3 className="text-xl font-semibold">2. Save Favorites</h3>
              <p className="text-gray-600">Save deals you love and get notified about similar offers</p>
            </div>

            <div className="text-center space-y-4" data-testid="step-3">
              <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-10 h-10 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold">3. Get Personalized</h3>
              <p className="text-gray-600">Receive AI-powered recommendations based on your preferences</p>
            </div>

            <div className="text-center space-y-4" data-testid="step-4">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="w-10 h-10 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold">4. Track Savings</h3>
              <p className="text-gray-600">Monitor your savings and see how much you've saved</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Brands/Partners */}
      <section className="py-16 bg-gray-50" data-testid="brands-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="brands-heading">
              Popular Brands
            </h2>
            <p className="text-gray-600 text-lg" data-testid="brands-description">
              Trusted by UAE's leading brands
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-white rounded-lg p-4 flex items-center justify-center hover:shadow-lg transition-shadow cursor-pointer"
                data-testid="brand-logo"
              >
                <img
                  src={brand.logo_url}
                  alt={brand.name}
                  className="w-full h-16 object-contain grayscale hover:grayscale-0 transition-all"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white" data-testid="value-proposition-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="value-proposition-heading">
                Why Choose Yalla ! Lets Save. ?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center space-y-3" data-testid="value-1">
                <CheckCircle className="w-12 h-12 mx-auto" />
                <h3 className="text-xl font-semibold">Verified Deals</h3>
                <p className="text-emerald-50">All deals are verified and updated daily</p>
              </div>

              <div className="text-center space-y-3" data-testid="value-2">
                <CheckCircle className="w-12 h-12 mx-auto" />
                <h3 className="text-xl font-semibold">Free to Use</h3>
                <p className="text-emerald-50">No subscription fees, completely free</p>
              </div>

              <div className="text-center space-y-3" data-testid="value-3">
                <CheckCircle className="w-12 h-12 mx-auto" />
                <h3 className="text-xl font-semibold">UAE-Wide Coverage</h3>
                <p className="text-emerald-50">Deals across all seven emirates</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
